import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { WishlistProvider } from './contexts/WishlistContext';

import ErrorBoundary from './components/ErrorBoundary';
import OfflineIndicator from './components/OfflineIndicator';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Auth/Login';
import { initGA } from './utils/analytics';
import { usePageTracking } from './hooks/usePageTracking';

const Home = lazy(() => import('./components/Home/Home'));
const CategoryList = lazy(() => import('./components/Category/CategoryList'));
const CategoryProducts = lazy(() => import('./components/Category/CategoryProducts'));
const AllProducts = lazy(() => import('./components/Product/AllProducts'));
const ProductDetail = lazy(() => import('./components/Product/ProductDetail'));
const Cart = lazy(() => import('./components/Cart/Cart'));
const Checkout = lazy(() => import('./components/Checkout/Checkout'));
const OrderConfirmation = lazy(() => import('./components/Order/OrderConfirmation'));
const OrdersList = lazy(() => import('./components/Order/OrdersList'));
const OrderDetail = lazy(() => import('./components/Order/OrderDetail'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const EditProfile = lazy(() => import('./components/Profile/EditProfile'));
const Wishlist = lazy(() => import('./components/Profile/Wishlist'));
const Support = lazy(() => import('./components/Profile/Support'));
const AddressManagement = lazy(() => import('./components/Profile/AddressManagement'));
const AddAddress = lazy(() => import('./components/Profile/AddAddress'));
const AuthTest = lazy(() => import('./components/AuthTest'));

const PrivacyPolicy = lazy(() => import('./components/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/Legal/TermsOfService'));
const RefundPolicy = lazy(() => import('./components/Legal/RefundPolicy'));
const BulkOrderInquiry = lazy(() => import('./components/Business/BulkOrderInquiry'));

const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-primary-25 via-white to-accent-25 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Analytics wrapper component for page tracking
const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  usePageTracking();
  return <>{children}</>;
};

const App: React.FC = () => {
  // Initialize GA4 once at app bootstrap
  useEffect(() => {
    initGA();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <Router>
              <AnalyticsWrapper>
              <div className="App">
                <OfflineIndicator />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {}
                  <Route path="/login" element={<Login />} />
                  <Route path="/test-auth" element={<AuthTest />} />
                  
                  {}
                  <Route path="/" element={<Home />} />
                  <Route path="/categories" element={<CategoryList />} />
                  <Route path="/category/:categoryId" element={<CategoryProducts />} />
                  <Route path="/products" element={<AllProducts />} />
                  <Route path="/product/:productId" element={<ProductDetail />} />
                  
                  {}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/bulk-orders" element={<BulkOrderInquiry />} />
                
                {}
                <Route path="/cart" element={<Cart />} />
                {}
                <Route path="/" element={<Home />} />
                <Route path="/categories" element={<CategoryList />} />
                <Route path="/category/:categoryId" element={<CategoryProducts />} />
                <Route path="/products" element={<AllProducts />} />
                <Route path="/product/:productId" element={<ProductDetail />} />
                
                {}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
              
              {}
              <Route path="/cart" element={<Cart />} />
              
              {}
              
              <Route path="/checkout" element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              } />
              
              <Route path="/order-confirmation/:orderId" element={
                <ProtectedRoute>
                  <OrderConfirmation />
                </ProtectedRoute>
              } />
              
              <Route path="/orders" element={
                <ProtectedRoute>
                  <OrdersList />
                </ProtectedRoute>
              } />
              
              <Route path="/order/:orderId" element={
                <ProtectedRoute>
                  <OrderDetail />
                </ProtectedRoute>
              } />
              
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              
              <Route path="/edit-profile" element={
                <ProtectedRoute>
                  <EditProfile />
                </ProtectedRoute>
              } />
              
              <Route path="/wishlist" element={
                <ProtectedRoute>
                  <Wishlist />
                </ProtectedRoute>
              } />
              
              <Route path="/support" element={
                <Support />
              } />
              
              <Route path="/addresses" element={
                <ProtectedRoute>
                  <AddressManagement />
                </ProtectedRoute>
              } />
              
              <Route path="/add-address" element={
                <ProtectedRoute>
                  <AddAddress />
                </ProtectedRoute>
              } />
              
              {}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            
            {}
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#363636',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '14px'
                },
                success: {
                  style: {
                    background: '#10b981',
                  },
                },
                error: {
                  style: {
                    background: '#ef4444',
                  },
                },
              }}
            />
          </div>
              </AnalyticsWrapper>
        </Router>
          </WishlistProvider>
      </CartProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
